// The client's side of an engagement (ADR-0015).
//
// One agency, one authorisation — and one Revoke that is a single transaction
// in the database rather than a loop over its people that can half-fail. That
// distinction is the whole reason this exists: a Revoke button that reports
// success over a partial removal leaves somebody with publishing rights and
// nobody looking for them.
//
// The form is deliberately about the envelope and not about people. The client
// says which roles, which accounts and until when; the agency decides who fills
// it. Naming individuals here would put the client back in the business of
// approving every hire the agency makes, which is what the sixty-invitation
// problem was.

import { useCallback, useEffect, useState } from "react";
import {
  authoriseAgency, clientEngagements, findAgency, revokeEngagement,
  type ClientEngagement, type EngagementScope,
} from "../lib/agency";
import { ROLE_LABEL } from "../lib/workspace";
import type { Connection } from "../lib/types";
import type { Role } from "../lib/people";
import { Loading, Spinner } from "./Spinner";

const shortDate = (iso: string) =>
  new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });

function dateInputValue(daysFromNow: number): string {
  const d = new Date();
  d.setDate(d.getDate() + daysFromNow);
  return d.toISOString().slice(0, 10);
}

const endOfDayIso = (v: string): string | null => {
  if (!v) return null;
  const d = new Date(`${v}T23:59:59`);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
};

export function AuthorisedAgencies({
  workspaceId, connections, roles, mayManage, onChanged,
}: {
  workspaceId: string;
  connections: Connection[];
  roles: Role[];
  mayManage: boolean;
  onChanged: () => void;
}) {
  const [list, setList] = useState<ClientEngagement[]>([]);
  const [error, setError] = useState<string | null>(null);
  // Which call is running, so the ring lands on the control that started it
  // rather than on all of them at once.
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const busy = busyKey !== null;
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);

  const [handle, setHandle] = useState("");
  const [found, setFound] = useState<{ name: string; handle: string } | null>(null);
  const [checked, setChecked] = useState(false);
  const [allowed, setAllowed] = useState<string[]>(["creator"]);
  const [scope, setScope] = useState<EngagementScope>("connections");
  const [accounts, setAccounts] = useState<string[]>([]);
  const [ends, setEnds] = useState(dateInputValue(365));

  const load = useCallback(async () => {
    setList(await clientEngagements(workspaceId));
  }, [workspaceId]);

  useEffect(() => {
    setLoading(true);
    load()
      .catch((e) => setError(e.message ?? String(e)))
      .finally(() => setLoading(false));
  }, [load]);

  async function run(fn: () => Promise<unknown>, key = "panel") {
    setBusyKey(key);
    setError(null);
    try {
      await fn();
      await load();
      onChanged();
    } catch (e: any) {
      setError(e.message ?? String(e));
    } finally {
      setBusyKey(null);
    }
  }

  const live = connections.filter((c) => c.status !== "revoked");
  const grantable = roles.filter((r) => !r.is_owner && r.code !== "admin");
  const ready = !!found && allowed.length > 0
    && (scope === "workspace" || accounts.length > 0);

  return (
    <div className="card">
      <div className="card-head">
        <h2>Authorised agencies</h2>
        {list.length > 0 && (
          <span className="muted">
            {list.length === 1 ? "1 agency" : `${list.length} agencies`}
          </span>
        )}
      </div>

      {error && <p className="err card-sub">{error}</p>}

      {loading && <Loading label="Loading authorised agencies…" />}

      {!loading && list.length === 0 && !open && (
        <p className="muted card-sub" style={{ marginBottom: mayManage ? ".8rem" : 0 }}>
          No agency is authorised here. Authorising one lets it put its own people on your accounts,
          within limits you set — and lets you end all of that in one action.
        </p>
      )}

      {!loading && list.map((e) => (
        <section className="outside-group" key={e.id}>
          <header className="outside-group-head">
            <div>
              <strong>{e.agency_name}</strong>
              {e.agency_handle && <span className="muted"> @{e.agency_handle}</span>}
              <span className="muted">
                {" · "}
                {e.people.length === 0
                  ? "nobody assigned yet"
                  : e.people.length === 1 ? "1 person" : `${e.people.length} people`}
                {" · "}
                {e.scope === "workspace"
                  ? "whole workspace"
                  : e.accounts.length === 1 ? e.accounts[0] : `${e.accounts.length} accounts`}
              </span>
            </div>
            <div className="outside-group-when">
              {e.expires_at
                ? <span className="chip">Until {shortDate(e.expires_at)}</span>
                : <span className="chip warn">No end date</span>}
            </div>
          </header>

          <div className="person-roles" style={{ margin: ".2rem 0 .5rem" }}>
            <span className="muted">
              May act as {e.allowed_roles.map((r) => ROLE_LABEL[r] ?? r).join(" or ")}
              {e.people.length > 0 && ` — currently ${e.people.join(", ")}`}
            </span>
          </div>

          {mayManage && (
            <button
              className={busyKey === `revoke:${e.id}` ? "sm danger is-busy" : "sm danger"}
              disabled={busy}
              onClick={() => {
                if (!confirm(
                  `End ${e.agency_name}'s access?\n\n`
                  + `${e.people.length} of their people lose access to your accounts immediately. `
                  + "This is one action in the database — there is no partly-revoked state.")) return;
                void run(() => revokeEngagement(e.id), `revoke:${e.id}`);
              }}
            >
              {busyKey === `revoke:${e.id}` && <Spinner />}
              {busyKey === `revoke:${e.id}` ? "Revoking…" : "Revoke"}
            </button>
          )}
        </section>
      ))}

      {mayManage && !open && (
        <button className="sm" onClick={() => setOpen(true)}>Authorise an agency</button>
      )}

      {mayManage && open && (
        <div className="authorise-form">
          <div className="row">
            <input
              type="text"
              value={handle}
              placeholder="their handle, e.g. bluepeak"
              aria-label="Agency handle"
              style={{ maxWidth: "16rem" }}
              onChange={(e) => { setHandle(e.target.value.toLowerCase()); setChecked(false); setFound(null); }}
            />
            <button
              className={busyKey === "lookup" ? "sm is-busy" : "sm"}
              disabled={busy || !handle.trim()}
              onClick={() => run(async () => {
                const hit = await findAgency(handle);
                setFound(hit);
                setChecked(true);
              }, "lookup")}
            >
              {busyKey === "lookup" && <Spinner />}
              {busyKey === "lookup" ? "Looking up…" : "Look up"}
            </button>
          </div>

          {/* Confirming the name before granting anything: a handle is easy to
              mistype and this is publishing access to someone's audience. */}
          {checked && !found && (
            <p className="err card-sub">
              No agency has the handle “{handle}”. Ask them to confirm it — they can see it on their
              own Clients screen.
            </p>
          )}

          {found && (
            <>
              <p className="card-sub">
                Authorising <strong>{found.name}</strong> (@{found.handle}).
              </p>

              <fieldset className="relationship-choice">
                <legend>What may their people do?</legend>
                <div className="role-ticks">
                  {grantable.map((r) => (
                    <label key={r.code} className="tick">
                      <input
                        type="checkbox"
                        checked={allowed.includes(r.code)}
                        onChange={(ev) => setAllowed((cur) =>
                          ev.target.checked ? [...cur, r.code] : cur.filter((c) => c !== r.code))}
                      />
                      <span>{r.name}</span>
                    </label>
                  ))}
                </div>
              </fieldset>

              <fieldset className="relationship-choice">
                <legend>On what?</legend>
                <label className={scope === "connections" ? "method on" : "method"}>
                  <input type="radio" name="eng-scope" checked={scope === "connections"}
                         onChange={() => setScope("connections")} />
                  <span>
                    <strong>Only the accounts I choose</strong>
                    <span className="muted">They cannot reach anything else here.</span>
                  </span>
                </label>
                <label className={scope === "workspace" ? "method on" : "method"}>
                  <input type="radio" name="eng-scope" checked={scope === "workspace"}
                         onChange={() => setScope("workspace")} />
                  <span>
                    <strong>The whole workspace</strong>
                    <span className="muted">Everything their roles allow, including accounts you add later.</span>
                  </span>
                </label>
              </fieldset>

              {scope === "connections" && (
                <div className="role-ticks card-sub">
                  {live.length === 0 && (
                    <p className="muted">No connected accounts yet — connect one first.</p>
                  )}
                  {live.map((c) => (
                    <label key={c.id} className="tick">
                      <input
                        type="checkbox"
                        checked={accounts.includes(c.id)}
                        onChange={(ev) => setAccounts((cur) =>
                          ev.target.checked ? [...cur, c.id] : cur.filter((x) => x !== c.id))}
                      />
                      <span>{c.display_name}</span>
                    </label>
                  ))}
                </div>
              )}

              <div className="row card-sub">
                <label htmlFor="eng-ends" style={{ margin: 0 }}>Ends</label>
                <input id="eng-ends" type="date" value={ends} min={dateInputValue(1)}
                       style={{ maxWidth: "12rem" }}
                       onChange={(e) => setEnds(e.target.value)} />
                <span className="muted" style={{ fontSize: ".8rem" }}>
                  Nothing they assign can outlast this.
                </span>
              </div>

              <div className="row">
                <button
                  className={busyKey === "authorise" ? "primary is-busy" : "primary"}
                  disabled={busy || !ready}
                  onClick={() => run(async () => {
                    await authoriseAgency({
                      clientWorkspaceId: workspaceId,
                      handle,
                      allowedRoles: allowed,
                      scope,
                      connectionIds: scope === "connections" ? accounts : [],
                      expiresAt: endOfDayIso(ends),
                    });
                    setOpen(false);
                    setHandle(""); setFound(null); setChecked(false);
                    setAllowed(["creator"]); setAccounts([]);
                  }, "authorise")}
                >
                  {busyKey === "authorise" && <Spinner />}
                  {busyKey === "authorise" ? "Authorising…" : `Authorise ${found.name}`}
                </button>
                <button className="sm" disabled={busy} onClick={() => setOpen(false)}>Cancel</button>
              </div>
            </>
          )}

          {!found && (
            <button className="sm" disabled={busy} onClick={() => setOpen(false)}>Cancel</button>
          )}
        </div>
      )}
    </div>
  );
}
